import { isAxiosError } from "axios";

import { brilioApi } from "../config/axios";

/**
 * Typed wrappers around the Brilio backend endpoints this admin app uses.
 * Keep this file pure (no react), so it's reusable from tests/storybook.
 *
 * SCOPE (2026-07): this admin is deliberately limited to what the live `api/`
 * backend actually serves under `/shopify/admin/*` — the shop's status/plan/usage
 * and the two merchant-editable concierge fields (persona + greeting). The
 * richer analytics / billing / widget-settings / conversations surfaces the
 * earlier prototype assumed are NOT backed by the live API and were removed
 * rather than stubbed with placeholder data.
 */

/**
 * The install statuses the backend sets, in one place so no caller re-types them.
 *
 * Kept as an `as const` object rather than a TS `enum` so the values stay plain
 * strings on the wire, and so this file remains the single source the UI, the
 * recovery set and the tests all derive from.
 */
export const INSTALL_STATUS = {
  installing: "installing",
  active: "active",
  failed: "failed",
  uninstalled: "uninstalled",
  redacted: "redacted",
} as const;

/** Exactly the statuses this app knows about — no wider. */
export type KnownInstallStatus =
  (typeof INSTALL_STATUS)[keyof typeof INSTALL_STATUS];

// Free-form on purpose: the backend owns the status vocabulary, so an unknown
// value must still type and simply be displayed. `(string & {})` keeps the known
// literals for autocomplete/narrowing without collapsing the union to `string`.
export type InstallStatus = KnownInstallStatus | (string & {});

/**
 * The maximum number of starters the backend will accept. Mirrored here so the
 * editor can stop offering a row the save would reject.
 */
export const MAX_SUGGESTED_QUESTIONS = 4;

/**
 * The concierge settings AS THE API SENDS THEM.
 *
 * `is_active` and `suggested_questions` are optional here because a response
 * predating them is reachable: this SPA and the API deploy from separate
 * workflows (`core-brilio-shopify.yml` and `core-brilio-api.yml`), and a browser
 * can hold a cached build. This is the wire shape — `normaliseConcierge` turns
 * it into `ConciergeSettings` exactly once, at the boundary.
 */
export interface ConciergeSettingsWire {
  // Nullable: the backend returns nulls when the agent row is missing/soft-deleted.
  agent_name: string | null;
  persona: string | null;
  greeting: string | null;
  is_active?: boolean;
  suggested_questions?: string[];
  /**
   * Where a shopper's request for a human is emailed. THREE states on the wire,
   * and they are not interchangeable: `undefined` is an API build predating the
   * field, `null` is "never set", and `""` is the merchant having switched
   * notifications off. Only the last two are a merchant's answer.
   */
  handoff_email?: string | null;
}

/**
 * The concierge settings AS THE APP USES THEM — defaults already applied, so
 * every consumer reads the fields without a `??` of its own.
 *
 * This split is the point. Defaulting per call site is how the crash this fixes
 * happened: `Settings.tsx` read `suggested_questions` straight off the response
 * and `undefined.length` blanked the page. Applying defaults once, where the
 * JSON enters the app, means a new page cannot forget them — there is no
 * un-normalised value in scope to forget about.
 */
export interface ConciergeSettings extends ConciergeSettingsWire {
  /**
   * The merchant's pause switch. When false the concierge is off the storefront
   * entirely — the widget stops loading AND the chat endpoint stops answering.
   * The backend reports `false` when there is no agent row at all.
   */
  is_active: boolean;
  /**
   * Conversation starters shown before the shopper types. The backend
   * substitutes its own defaults when the merchant has never set them, and an
   * empty array is the merchant's own "show none".
   */
  suggested_questions: string[];
  /**
   * The notification address as the settings form uses it: `""` for both "never
   * set" and "switched off", because a text input has no third state to render.
   * The wire's `null`/`""` distinction is deliberately NOT carried here — a
   * caller that needs it (a Dashboard nudge for a shop taking handoffs with
   * nobody to send them to) reads `ShopMeWire`. This note exists so that caller
   * looks upstream instead of concluding the backend lost the distinction.
   */
  handoff_email: string;
}

/**
 * Apply the version-skew defaults. Called once per response, in this module.
 *
 * An absent `is_active` means the API predates the pause switch. In that API a
 * concierge was live exactly when it was PROVISIONED, so the fallback has to
 * answer "is there an agent?" rather than assume yes.
 *
 * `agent_name` is the signal, and it is the backend's own: `get_shop_admin_view`
 * sets `is_active = False` in the same branch that leaves `agent_name` null —
 * both mean "no agent row, nothing is answering shoppers". Deriving the fallback
 * from it makes this agree with what the current backend would have sent for the
 * same shop, instead of reporting Live for a shop with no concierge at all.
 *
 * A blanket `?? true` was the same class of lie this app has been removing all
 * week: a status computed from something that does not know the answer.
 */
function normaliseConcierge(wire: ConciergeSettingsWire): ConciergeSettings {
  return {
    ...wire,
    // `!= null`, not `!== null`: the loose form is the idiomatic nullish check
    // and catches BOTH null and undefined. A legacy response that omits
    // agent_name entirely would pass `!== null` and land back on "Live" — the
    // exact bug this line exists to fix, one shape further out.
    is_active: wire.is_active ?? wire.agent_name != null,
    suggested_questions: wire.suggested_questions ?? [],
    // `??`, so only null/undefined default — an explicit "" is the merchant's
    // own choice and survives untouched.
    handoff_email: wire.handoff_email ?? '',
  };
}

function normaliseShop(wire: ShopMeWire): ShopMe {
  return { ...wire, concierge: normaliseConcierge(wire.concierge) };
}

/** A partial edit — send only the fields being changed. */
export interface ConciergeUpdate {
  persona?: string;
  greeting?: string;
  agent_name?: string;
  is_active?: boolean;
  suggested_questions?: string[];
  /** `""` is a real edit — "stop emailing me" — not an absent field. */
  handoff_email?: string;
}

/** The shop as the API sends it — see `ConciergeSettingsWire`. */
export interface ShopMeWire {
  shop_domain: string;
  install_status: InstallStatus;
  plan: string;
  charge_status: string | null;
  messages_used: number;
  messages_quota: number;
  period_start: string | null;
  period_end: string | null;
  /**
   * Credits are what actually stop the storefront answering — `messages_used`
   * is a usage display. Optional on the wire because the extension and the API
   * deploy independently: an older backend simply omits them, and the UI must
   * degrade to "no warning" rather than to a broken page.
   */
  credits_remaining?: number;
  credit_state?: CreditState;
  /**
   * How many policy pages are already in the concierge's knowledge.
   *
   * Lets Settings tell "never indexed" from "indexed and up to date" without
   * guessing — a merchant should not have to press a button to find out
   * whether pressing it is necessary. Optional so an older backend degrades to
   * the neutral copy rather than to a broken page.
   */
  policies_indexed?: number;
  /**
   * The app's Shopify handle, used to build the managed-pricing URL. Optional so
   * an older backend degrades to hiding the upgrade control rather than sending
   * the merchant to a broken page.
   */
  app_handle?: string;
  concierge: ConciergeSettingsWire;
}

/** How close the shop is to going quiet. Ordered by urgency. */
export type CreditState = "ok" | "low" | "critical" | "empty";

/**
 * Outcome of a policy sync.
 *
 * Every one of these is a 200 — only `indexed` did work, and the others are
 * facts about the merchant's store or install with different next actions.
 * `scope_missing` in particular has NOT failed: it means the store installed
 * the app before it asked to read policies, and the fix is reopening the app
 * to approve the permission, not rewriting policies that already exist.
 *
 * Policies are read through the Admin API, so a password-protected storefront
 * is no longer a distinct outcome — the read works on a gated store.
 */
export type PolicySyncStatus =
  | "indexed"
  | "no_policies_published"
  | "scope_missing"
  | "shopify_unreachable"
  | "failed";

export interface PolicySyncResult {
  status: PolicySyncStatus;
  indexed: string[];
  /** Policies the sync could not read this run; named in `message` too. */
  failed: string[];
  message: string;
}

/** The shop as the app uses it — concierge defaults already applied. */
export interface ShopMe extends ShopMeWire {
  concierge: ConciergeSettings;
}

// --- Endpoints ---------------------------------------------------------------
// Both are session-token authenticated (App Bridge JWT); the backend resolves
// the shop from the signed `dest` claim — never from anything the SPA sends.

export async function getShopMe(): Promise<ShopMe> {
  const { data } = await brilioApi.get<ShopMeWire>("/shopify/admin/shops/me");
  return normaliseShop(data);
}

/**
 * Finish a managed install for the current shop.
 *
 * Shopify grants our scopes itself and then simply loads this app — it never
 * calls the backend's OAuth install route, so on a real install no shop row
 * exists yet and `getShopMe` 404s. The backend trades the session token this
 * request already carries for an offline access token and provisions the shop.
 *
 * Idempotent: calling it for an already-installed shop just refreshes the token.
 */
export async function bootstrapInstall(): Promise<ShopMe> {
  const { data } = await brilioApi.post<ShopMeWire>(
    "/shopify/admin/shops/me/install",
  );
  return normaliseShop(data);
}

/**
 * Install states the app can recover from by re-running the bootstrap.
 *
 * `uninstalled` is the important one: uninstalling does NOT delete the shop row,
 * it only flips this status and revokes the token. So a merchant who reinstalls
 * gets `200` from `/shops/me`, not `404` — and if only a 404 triggered the
 * bootstrap they would be left staring at a dead app with a revoked token
 * forever. The backend already re-arms the shop inside `provision_shop`; nothing
 * was calling it.
 *
 * `redacted` was deliberately absent, on the reasoning that such a shop's data
 * was erased on a GDPR request and silently re-provisioning it is not the app's
 * call to make. Sound reasoning, wrong premise: Shopify fires `shop/redact`
 * about 48 hours after EVERY uninstall, not only when a merchant asks to be
 * forgotten. So the state does not mean "this merchant demanded erasure", it
 * means "this merchant uninstalled two days ago" — and excluding it left anyone
 * who reinstalled later staring at a permanently dead app, with no way out.
 *
 * Re-provisioning is safe and is not a resurrection of erased data: the backend
 * has already erased it. `provision_shop` re-arms the row with a fresh token,
 * and `activate_concierge` builds a NEW agent and knowledge base — `shop_redact`
 * nulls `agent_id`, which is that method's sole idempotency key. The merchant
 * gets a working, empty concierge, which is exactly what a reinstall should be.
 */
const RECOVERABLE_INSTALL_STATES = [
  INSTALL_STATUS.uninstalled,
  INSTALL_STATUS.failed,
  INSTALL_STATUS.redacted,
] as const satisfies readonly KnownInstallStatus[];

/**
 * The tuple above is what gets compile-time checked — `satisfies` makes a typo
 * in it a build error rather than a branch that silently never fires. This Set
 * is derived from it purely for lookup, so the two can never drift.
 *
 * Typed `Set<string>` deliberately: the value tested against it is free-form by
 * design (the backend owns the status vocabulary and may add one this app does
 * not know yet), so accepting a string here is the honest signature — and it
 * avoids casting at the call site.
 */
const RECOVERABLE_LOOKUP: ReadonlySet<string> = new Set(
  RECOVERABLE_INSTALL_STATES,
);

function isRecoverable(status: InstallStatus): boolean {
  return RECOVERABLE_LOOKUP.has(status);
}

/**
 * The shop as the embedded app needs it: load it, complete the install if this
 * is a fresh managed install (no shop row) or a reinstall (row present but not
 * live), then return it.
 *
 * On the error path only a 404 bootstraps — a 401 means the session token was
 * rejected, and any other error is a real failure that should surface rather
 * than be retried as an install.
 */
export async function getOrBootstrapShop(): Promise<ShopMe> {
  let shop: ShopMe;
  try {
    // `await` is load-bearing here, not noise: without it the promise is
    // returned before it settles and a rejection escapes this try/catch, so a
    // fresh install would surface as an error instead of bootstrapping.
    shop = await getShopMe();
  } catch (err) {
    if (isNotFound(err)) return bootstrapInstall();
    throw err;
  }
  return isRecoverable(shop.install_status) ? bootstrapInstall() : shop;
}

function isNotFound(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 404;
}

/**
 * Does this payload actually ask for a change?
 *
 * Presence, NOT truthiness. `is_active: false` (take the concierge off the
 * storefront) and `suggested_questions: []` (show no starters) are the two most
 * consequential edits a merchant can make, and both are falsy — a truthiness
 * check would reject precisely those while happily forwarding a whitespace-only
 * persona the backend is guaranteed to 422.
 *
 * The text fields are the other way round: a blank one is not an edit, it's an
 * empty box, so it doesn't count towards "did you ask for anything?".
 *
 * Note this guard is deliberately STRICTER than the server, not a mirror of it:
 * `min_length=1` counts characters, so the backend would actually accept a
 * whitespace-only persona and store it verbatim. Trimming here keeps the admin
 * from ever sending one.
 */
function hasEdit(payload: ConciergeUpdate): boolean {
  if (payload.is_active !== undefined) return true;
  if (payload.suggested_questions !== undefined) return true;
  // Tested against `undefined`, NOT folded into the truthiness block below.
  // Clearing the box is the merchant switching notifications off, and `""` is
  // falsy — so `Boolean(...)` would silently drop the one edit a merchant makes
  // when handoff mail starts bothering them, and the UI would report a save
  // that never left the browser.
  if (payload.handoff_email !== undefined) return true;
  return Boolean(
    payload.persona?.trim() ||
    payload.greeting?.trim() ||
    payload.agent_name?.trim(),
  );
}

/**
 * Read the store's own policies into the concierge's knowledge.
 *
 * Read through Shopify's Admin API, not the public storefront, so it works on a
 * password-protected development store.
 *
 * The endpoint answers 200 for every outcome that is not an outright fault,
 * including the ones where nothing was indexed, so the caller renders
 * `message` rather than deciding what happened from a status code.
 */
export async function syncPolicyKnowledge(): Promise<PolicySyncResult> {
  const { data } = await brilioApi.post<PolicySyncResult>(
    "/shopify/admin/shops/me/knowledge/policies",
  );
  return data;
}

/** Edit any of the merchant-owned concierge settings; returns the refreshed shop. */
export async function updateConcierge(
  payload: ConciergeUpdate,
): Promise<ShopMe> {
  // The backend requires at least one field. Fail fast on an empty payload so
  // any caller gets a clear error instead of a round-trip to a guaranteed 422.
  if (!hasEdit(payload)) {
    throw new Error(
      "Provide at least one of: persona, greeting, agent_name, is_active, suggested_questions.",
    );
  }
  const { data } = await brilioApi.patch<ShopMeWire>(
    "/shopify/admin/shops/me/concierge",
    payload,
  );
  return normaliseShop(data);
}

/** One row of the merchant's conversation list. */
export interface ConversationListItem {
  uuid: string;
  started_at: string;
  /** Derived from the turns, not from `conversation.last_message_at` — that
   *  column is maintained best-effort by the backend and drifts. */
  last_message_at: string | null;
  turns: number;
  /** The shopper's opening message, whitespace-collapsed and length-capped.
   *  Used as the row's title because every storefront conversation is stored
   *  under the same name ("New Conversation"). */
  opening_question: string;
  asked_for_human: boolean;
  /** The reply address the shopper left when asking for a person, or null.
   *  This is the whole point of the handoff feature — the listing promises
   *  "you get the conversation and the address" — and it was captured but
   *  never returned by the API until now. */
  initiator_email: string | null;
}

export interface ConversationList {
  items: ConversationListItem[];
  total: number;
  page: number;
  page_size: number;
}

/** One exchange. An interaction row is a TURN, not a message. */
export interface ConversationTurn {
  uuid: string;
  asked_at: string;
  question: string;
  /** Null on a handoff turn: the shopper asked for a person and is waiting. */
  answer: string | null;
  asked_for_human: boolean;
}

export interface ConversationDetail {
  uuid: string;
  started_at: string;
  initiator_email: string | null;
  turns: ConversationTurn[];
}

/**
 * What shoppers have been asking, newest first.
 *
 * No shop parameter by design — the backend scopes every row to the shop named
 * in the session token this request already carries, so there is nothing here
 * that could address another merchant's history.
 */
export async function listConversations(
  params: {
    page?: number;
    pageSize?: number;
    q?: string;
  } = {},
): Promise<ConversationList> {
  const { data } = await brilioApi.get<ConversationList>(
    "/shopify/admin/shops/me/conversations",
    {
      params: {
        page: params.page ?? 1,
        page_size: params.pageSize ?? 25,
        // Omitted rather than sent empty: `q=` would be a search for the empty
        // string, and the backend would treat it as a filter.
        ...(params.q && params.q.trim() ? { q: params.q.trim() } : {}),
      },
    },
  );
  return data;
}

/** One dialog in full. 404 for anything outside this shop. */
export async function getConversation(
  uuid: string,
): Promise<ConversationDetail> {
  const { data } = await brilioApi.get<ConversationDetail>(
    `/shopify/admin/shops/me/conversations/${encodeURIComponent(uuid)}`,
  );
  return data;
}

/**
 * Where a merchant goes to choose or change their plan.
 *
 * This app bills through Shopify MANAGED PRICING: the merchant picks a plan on
 * Shopify's own page and `app_subscriptions/update` tells the backend. So an
 * upgrade is a redirect to that page, not a Billing API mutation — there is no
 * charge for this app to create.
 *
 * Returns null when either half is missing, so the caller hides the control
 * rather than offering a link to nowhere.
 */
export function pricingPlansUrl(
  shopDomain: string | undefined,
  appHandle: string | undefined,
): string | null {
  if (!shopDomain || !appHandle) return null;
  // admin.shopify.com keys stores by the handle, i.e. the subdomain of
  // {store}.myshopify.com — not the full domain.
  const store = shopDomain.replace(/\.myshopify\.com$/i, "").trim();
  if (!store || store.includes("/")) return null;
  return `https://admin.shopify.com/store/${encodeURIComponent(store)}/charges/${encodeURIComponent(appHandle)}/pricing_plans`;
}
